import { type Signal, computed } from "@angular/core";
import { SimpleFilter } from "./simple-filter";
import { Filter } from "./filter";
import type { FilterSelectionList, Predicate, DynamicFilters, DynamicSelection, FilterSelectionInput } from "@filter/filter.types";

export abstract class DynamicFilter<TSelection extends FilterSelectionList, TItem> extends Filter<TSelection, TItem> {
    public readonly isActive = computed(() => this.filters().some((field) => field.isActive()));

    // Filters is used to manage dynamic filters
    protected abstract filtersByName: Signal<DynamicFilters<TSelection, TItem>>;
    protected filters = computed(() => Array.from(this.filtersByName().values()));
    public getFilter(dynamicFilterName: string): SimpleFilter<TSelection, TItem> | undefined {
        return this.filtersByName().get(dynamicFilterName);
    }

    // The selection represents the current state of the filter.
    public readonly selection = computed(() => {
        const selections: DynamicSelection<TSelection> = {};
        const filters = this.filters();
        filters.forEach((filter) => (selections[filter.definition.filterName] = filter.selection()));
        return selections;
    });
    public clear(): void {
        this.filters().forEach((filter) => filter.clear());
    }
    public setSelection(value: FilterSelectionInput): void {
        const selections = this.isDynamicSelection(value) ? value : undefined;
        this.filters().forEach((filter) => filter.setSelection(selections?.[filter.definition.filterName]));
    }

    // A dynamic selection is a plain record keyed by child filter name, so it never carries a "kind" discriminant.
    private isDynamicSelection(value: FilterSelectionInput): value is DynamicSelection<TSelection> {
        return !!value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date) && !("kind" in value);
    }

    // The predicates are used to determine if an item satisfies the filter criteria.
    // Child predicates are resolved once so that filtering a list does not rebuild them for every item.
    protected getPredicate(): Predicate<TItem> | null {
        const predicates = this.filters()
            .map((filter) => filter.predicate())
            .filter((predicate): predicate is Predicate<TItem> => predicate !== null);
        if (!predicates.length) return null;

        return (item: TItem) => predicates.every((predicate) => predicate(item));
    }
    public predicate(): Predicate<TItem> | null {
        return this.getPredicate();
    }
}
